/**
 * Sentry Error Tracking & Performance Monitoring
 * EU Instance (Germany) - GDPR Compliant
 *
 * Integrations:
 * - Browser Tracing: Performance monitoring for API calls and navigation
 * - Session Replay: 10% of sessions, 100% on errors
 *
 * Privacy:
 * - beforeSend hook filters sensitive data (tokens, API keys, PATs)
 * - PII collection enabled for user context only
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production';
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE;

let sentryInitialized = false;

/**
 * Initialize Sentry error tracking
 * Safe to call multiple times - will only initialize once
 * Call this BEFORE React renders (in main.jsx)
 */
export function initSentry() {
  if (typeof window === 'undefined') return;
  if (sentryInitialized) return;

  if (!SENTRY_DSN) {
    console.warn('[Sentry] Missing DSN - error tracking disabled');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE,

      integrations: [
        Sentry.browserTracingIntegration({
          tracePropagationTargets: [
            'localhost',
            /^https:\/\/tafnybfhwybgijozwfyq\.supabase\.co/,
          ],
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],

      // Performance Monitoring
      tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,

      // Session Replay — free plan has 50 replays/month shared by promptnote-web,
      // reedle-web and mellow-web (org ChenyOrgCn). Dev never records (you are at
      // the screen); production keeps every error session and a 5% sample of the
      // rest so the quota lasts the whole cycle. Decided 2026-10-07.
      replaysSessionSampleRate: SENTRY_ENVIRONMENT.startsWith('production') ? 0.05 : 0,
      replaysOnErrorSampleRate: SENTRY_ENVIRONMENT.startsWith('production') ? 1.0 : 0,

      sendDefaultPii: true,

      // Privacy: Filter sensitive data before sending
      beforeSend(event) {
        if (event.request?.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
          delete event.request.headers['apikey'];
        }

        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
            if (breadcrumb.data?.headers) {
              delete breadcrumb.data.headers.Authorization;
              delete breadcrumb.data.headers.authorization;
              delete breadcrumb.data.headers['apikey'];
            }
            return breadcrumb;
          });
        }

        if (event.contexts?.runtime?.env) {
          const env = event.contexts.runtime.env;
          Object.keys(env).forEach(key => {
            if (key.includes('KEY') || key.includes('SECRET') || key.includes('TOKEN') || key.includes('PAT')) {
              delete env[key];
            }
          });
        }

        return event;
      },

      ignoreErrors: [
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        'NetworkError',
        'Failed to fetch',
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],
    });

    sentryInitialized = true;
    console.log('[Sentry] Initialized successfully');
  } catch (error) {
    console.error('[Sentry] Initialization failed:', error);
  }
}

export function isSentryInitialized() {
  return sentryInitialized;
}

export { Sentry };
